import { useEffect, useRef, useState } from "react";

/**
 * Custom hook for scroll-triggered animations (compatible with Framer Motion v4)
 * @param {Object} options - Configuration options
 * @param {boolean} options.once - Whether animation should trigger only once (default: true)
 * @param {string} options.margin - Margin before triggering (default: "-100px")
 * @returns {Object} - { ref, isInView }
 */
export const useScrollAnimation = (options = {}) => {
    const { once = true, margin = "-100px" } = options;
    const ref = useRef(null);
    const [isInView, setIsInView] = useState(false);
    const [hasBeenInView, setHasBeenInView] = useState(false);

    useEffect(() => {
        const element = ref.current;
        if (!element) return;

        // Parse margin value
        const marginValue = parseInt(margin) || -100;

        const observer = new IntersectionObserver(
            ([entry]) => {
                const inView = entry.isIntersecting;

                if (inView && !hasBeenInView) {
                    setIsInView(true);
                    setHasBeenInView(true);
                } else if (!once && !inView) {
                    setIsInView(false);
                }
            },
            {
                rootMargin: `${marginValue}px 0px`,
                threshold: 0.1,
            }
        );

        observer.observe(element);

        return () => {
            if (element) {
                observer.unobserve(element);
            }
        };
    }, [once, margin, hasBeenInView]);

    return { ref, isInView };
};

/**
 * Common animation variants for reuse
 */
export const animationVariants = {
    fadeIn: {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        transition: { duration: 0.6 },
    },
    fadeInUp: {
        initial: { opacity: 0, y: 30 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.6, ease: "easeOut" },
    },
    fadeInDown: {
        initial: { opacity: 0, y: -30 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.6, ease: "easeOut" },
    },
    fadeInLeft: {
        initial: { opacity: 0, x: -50 },
        animate: { opacity: 1, x: 0 },
        transition: { duration: 0.6, ease: "easeOut" },
    },
    fadeInRight: {
        initial: { opacity: 0, x: 50 },
        animate: { opacity: 1, x: 0 },
        transition: { duration: 0.6, ease: "easeOut" },
    },
    scaleIn: {
        initial: { opacity: 0, scale: 0.8 },
        animate: { opacity: 1, scale: 1 },
        transition: { duration: 0.5, ease: "easeOut" },
    },
    slideInUp: {
        initial: { y: 100, opacity: 0 },
        animate: { y: 0, opacity: 1 },
        transition: { duration: 0.7, ease: "easeOut" },
    },
};

/**
 * Stagger children animation helper
 * @param {number} staggerDelay - Delay between each child (default: 0.1)
 */
export const staggerChildren = (staggerDelay = 0.1) => ({
    animate: {
        transition: {
            staggerChildren: staggerDelay,
        },
    },
});
